import { LATEST_DATA } from 'api-validations/stitch'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { Enum, isNonNil, Num, throttle } from 'utils'
import { shallow } from 'zustand/shallow'

import { MapMode, type MapState } from '.'
import { useOrgCodeOrUndefined } from '../../active-operation'
import { useApiClient, useQueryClient } from '../../api-provider'
import { reportMapArrivalFailure, setMapArrival } from './arrival'
import { useMapStateStore } from './core'
import {
  isTerrainLayer,
  type LayerSelection,
  SatelliteImagery,
  SoilSurveyAttribute,
  type TerrainLayer
} from './data-layer-state'
import {
  type BrowseFields,
  type BrowseListView,
  defaultBrowseFields,
  type FilterState,
  noFilter,
  Pane
} from './session'
import type { BrowseAddress, LayerAddress, MapAddress } from './transitions/address'
import { createMapCatalog } from './transitions/createMapCatalog'
import { runResolution } from './transitions/runResolution'

enum SearchParams {
  // AsApplied layer (main pane)
  StreamId = 'streamId',
  ApplicationId = 'applicationId',
  LayerId = 'layerId',
  MergedStreamId = 'mergedStreamId',
  // AsApplied layer (comparison pane)
  ComparisonStreamId = 'compareStreamId',
  ComparisonApplicationId = 'compareApplicationId',
  ComparisonLayerId = 'compareLayerId',
  ComparisonMergedStreamId = 'compareMergedStreamId',
  // Planning and Terrain layers (main pane). Both are scoped to a planning
  // field, so both read that id from the same param; `assetType` marks a plan
  // asset and `terrain` a terrain source, and a pane carries one or the other.
  PlanningFieldId = 'planningFieldId',
  PlanId = 'planId',
  AssetType = 'assetType',
  AssetId = 'assetId',
  TerrainSource = 'terrain',
  Imagery = 'imagery',
  CaptureDate = 'captureDate',
  SamplingEventId = 'samplingEventId',
  Measurement = 'measurement',
  SoilSurveyAttribute = 'soilAttribute',
  // Planning and Terrain layers (comparison pane)
  ComparisonPlanningFieldId = 'comparePlanningFieldId',
  ComparisonPlanId = 'comparePlanId',
  ComparisonAssetType = 'compareAssetType',
  ComparisonAssetId = 'compareAssetId',
  ComparisonTerrainSource = 'compareTerrain',
  ComparisonImagery = 'compareImagery',
  ComparisonCaptureDate = 'compareCaptureDate',
  ComparisonSamplingEventId = 'compareSamplingEventId',
  ComparisonMeasurement = 'compareMeasurement',
  ComparisonSoilSurveyAttribute = 'compareSoilAttribute',
  // Pane + cross-cutting
  SplitHidden = 'splitHidden',
  Record = 'record',
  MapMode = 'mode',
  ScoutingNoteId = 'noteId',
  // Field by name (main pane only; an entry form for links from other pages)
  Client = 'client',
  Farm = 'farm',
  Field = 'field',
  // BrowseFields
  ClientFilter = 'clientFilter',
  FarmFilter = 'farmFilter',
  SeasonFilterYear = 'seasonYear',
  SeasonFilterCrop = 'seasonCrop',
  ListView = 'view'
}

const ASSET_TYPES = ['Boundary', 'Prescription', 'GuidancePlan'] as const
type AssetType = (typeof ASSET_TYPES)[number]
const isAssetType = (s: string | null): s is AssetType =>
  s !== null && (ASSET_TYPES as readonly string[]).includes(s)

/** Pseudo asset-types marking the editors in the URL's planning params. */
const PLAN_EDITOR_ASSET_TYPE = 'PlanEditor'
const BOUNDARY_CREATOR_ASSET_TYPE = 'BoundaryCreator'
const PRESCRIPTION_CREATOR_ASSET_TYPE = 'PrescriptionCreator'

/** Terrain's URL discriminator is the variant's own `kind`, so the two can't drift. */
type TerrainSourceParam = TerrainLayer['kind']
const TERRAIN_SOURCES: readonly TerrainSourceParam[] = ['Satellite', 'SoilSample', 'SoilSurvey']
const isTerrainSource = (s: string | null): s is TerrainSourceParam =>
  s !== null && (TERRAIN_SOURCES as readonly string[]).includes(s)

const LIST_VIEWS = ['ByTime', 'ByField'] as const
const isListView = (s: string | null): s is BrowseListView =>
  s !== null && (LIST_VIEWS as readonly string[]).includes(s)

// ---------------------------------------------------------------------------
// URL → store
// ---------------------------------------------------------------------------

/**
 * One pane's address as the URL carries it, or `null` when the URL names no
 * layer for that pane.
 *
 * Reading produces an address rather than a selection because closing what a
 * link leaves open needs lookups — a stream's default layer, the plan that
 * references an asset — which only a resolution can perform. What the params
 * *do* name is read here; what they do not is left absent for `resolve` to
 * settle.
 */
const readLayerAddress = (searchParams: URLSearchParams, prefix: Pane): LayerAddress | null => {
  const get = (main: SearchParams, comp: SearchParams) =>
    searchParams.get(prefix === Pane.Main ? main : comp)
  const getAll = (main: SearchParams, comp: SearchParams) =>
    searchParams.getAll(prefix === Pane.Main ? main : comp)

  const streamId = get(SearchParams.StreamId, SearchParams.ComparisonStreamId)
  if (isNonNil(streamId)) {
    const applicationId = get(SearchParams.ApplicationId, SearchParams.ComparisonApplicationId)
    const layerId = get(SearchParams.LayerId, SearchParams.ComparisonLayerId)
    return {
      kind: 'Stream',
      streamId,
      // A layer id means nothing without the application it belongs to, and
      // naming neither leaves the stream's own default layer to resolution.
      layer: isNonNil(applicationId) && isNonNil(layerId) ? { applicationId, layerId } : undefined,
      mergedStreamIds: getAll(SearchParams.MergedStreamId, SearchParams.ComparisonMergedStreamId)
    }
  }

  // A planning field id on its own is an address: the field, with nothing over
  // it. It is also where the params below land when they name a layer only
  // partially — the field is the part that did resolve, and it is somewhere to
  // be, so nothing here falls back to the field list.
  const planningFieldId = get(SearchParams.PlanningFieldId, SearchParams.ComparisonPlanningFieldId)
  if (isNonNil(planningFieldId)) {
    const terrainSource = get(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource)
    if (isTerrainSource(terrainSource)) {
      if (terrainSource === 'Satellite') {
        const imagery = get(SearchParams.Imagery, SearchParams.ComparisonImagery)
        if (!Enum.isValidValue(imagery, SatelliteImagery)) return { kind: 'Field', planningFieldId }
        return {
          kind: 'Satellite',
          planningFieldId,
          imagery,
          captureDate:
            get(SearchParams.CaptureDate, SearchParams.ComparisonCaptureDate) ?? undefined
        }
      }

      if (terrainSource === 'SoilSurvey') {
        const attribute = get(
          SearchParams.SoilSurveyAttribute,
          SearchParams.ComparisonSoilSurveyAttribute
        )
        if (!Enum.isValidValue(attribute, SoilSurveyAttribute)) {
          return { kind: 'Field', planningFieldId }
        }
        return { kind: 'SoilSurvey', planningFieldId, attribute }
      }

      const samplingEventId = get(
        SearchParams.SamplingEventId,
        SearchParams.ComparisonSamplingEventId
      )
      const measurement = get(SearchParams.Measurement, SearchParams.ComparisonMeasurement)
      if (!isNonNil(samplingEventId) || !isNonNil(measurement)) {
        return { kind: 'Field', planningFieldId }
      }
      return { kind: 'SoilSample', planningFieldId, samplingEventId, measurement }
    }

    const assetTypeRaw = get(SearchParams.AssetType, SearchParams.ComparisonAssetType)
    const planId = get(SearchParams.PlanId, SearchParams.ComparisonPlanId) ?? undefined
    // The plan editor is a main-pane-only state; never restore it on comparison.
    // Its plan cannot be looked up either — an unnamed one means a new plan, not
    // an unknown one.
    if (assetTypeRaw === PLAN_EDITOR_ASSET_TYPE) {
      return prefix === Pane.Main
        ? { kind: 'PlanEditor', planningFieldId, planId: planId ?? null }
        : null
    }
    if (assetTypeRaw === BOUNDARY_CREATOR_ASSET_TYPE) {
      return prefix === Pane.Main ? { kind: 'BoundaryCreator', planningFieldId } : null
    }
    if (assetTypeRaw === PRESCRIPTION_CREATOR_ASSET_TYPE) {
      return prefix === Pane.Main ? { kind: 'PrescriptionCreator', planningFieldId } : null
    }
    if (!isAssetType(assetTypeRaw)) return { kind: 'Field', planningFieldId }

    if (assetTypeRaw === 'GuidancePlan') {
      return {
        kind: 'PlanAsset',
        planningFieldId,
        planId,
        asset: { assetType: 'GuidancePlan' }
      }
    }
    const assetId = get(SearchParams.AssetId, SearchParams.ComparisonAssetId)
    if (!isNonNil(assetId)) return { kind: 'Field', planningFieldId }
    return {
      kind: 'PlanAsset',
      planningFieldId,
      planId,
      asset: { assetType: assetTypeRaw, assetId }
    }
  }

  if (prefix === Pane.Main) {
    const client = searchParams.get(SearchParams.Client)
    const farm = searchParams.get(SearchParams.Farm)
    const field = searchParams.get(SearchParams.Field)
    if (isNonNil(client) && isNonNil(farm) && isNonNil(field)) {
      return { kind: 'Cff', cff: { client, farm, field } }
    }
  }

  return null
}

/**
 * The whole URL as one address. Total by construction: a URL naming no layer is
 * the fields list, which is a place too.
 */
export const readMapAddress = (searchParams: URLSearchParams): MapAddress => {
  const main = readLayerAddress(searchParams, Pane.Main)
  if (main === null) return { kind: 'Browse', ...readBrowseAddress(searchParams) }

  const comparison = readLayerAddress(searchParams, Pane.Comparison)
  const recordRaw = searchParams.get(SearchParams.Record)
  const record = recordRaw === null ? undefined : Number(recordRaw)
  const mode = searchParams.get(SearchParams.MapMode)
  const activeNoteId = searchParams.get(SearchParams.ScoutingNoteId)

  return {
    kind: 'InField',
    main,
    comparison:
      comparison === null ||
      comparison.kind === 'PlanEditor' ||
      comparison.kind === 'BoundaryCreator' ||
      comparison.kind === 'PrescriptionCreator' ||
      comparison.kind === 'Cff'
        ? undefined
        : {
            layer: comparison,
            hidden: searchParams.get(SearchParams.SplitHidden) === '1'
          },
    record: Num.isPositiveInteger(record) ? record : undefined,
    mode: Enum.isValidValue(mode, MapMode) ? mode : undefined,
    activeNoteId: activeNoteId ?? undefined
  }
}

export const urlHasLayerSelection = (searchParams: URLSearchParams): boolean =>
  readMapAddress(searchParams).kind === 'InField'

const readBrowseAddress = (searchParams: URLSearchParams): BrowseAddress => {
  const browse = readBrowseFields(searchParams)
  return {
    filter: browse.filter,
    seasonFilter: browse.seasonFilter ?? undefined,
    listView: browse.listView
  }
}

const readBrowseFields = (searchParams: URLSearchParams): BrowseFields => {
  const client = searchParams.get(SearchParams.ClientFilter)
  const farm = searchParams.get(SearchParams.FarmFilter)
  const filter: FilterState = isNonNil(client)
    ? isNonNil(farm)
      ? { level: 'Farm', client, farm }
      : { level: 'Client', client }
    : noFilter

  const yearRaw = searchParams.get(SearchParams.SeasonFilterYear)
  const cropKey = searchParams.get(SearchParams.SeasonFilterCrop)
  const year = Num.isPositiveInteger(yearRaw === null ? null : Number(yearRaw))
    ? Number(yearRaw)
    : null
  const seasonFilter = year !== null && isNonNil(cropKey) ? { year, cropKey } : null

  const viewRaw = searchParams.get(SearchParams.ListView)
  const listView: BrowseListView = isListView(viewRaw) ? viewRaw : 'ByField'

  return { kind: 'BrowseFields', filter, seasonFilter, listView }
}

/**
 * Land the store on whatever the URL names, resolving what the URL leaves open.
 *
 * A URL naming something the operation does not have resolves to nothing. That
 * is not a state to render, so it lands on the fields list and reports the
 * failure through {@link useMapArrival} for the page to say so.
 */
export const useSyncSearchParams = () => {
  const location = useLocation()
  const orgCode = useOrgCodeOrUndefined()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  // The operation we last resolved in, because the same params name different
  // data in another one.
  const resolvedOrgCode = useRef<string | null>(null)

  useEffect(() => {
    // Off the map route there is nothing to resolve, and the store should stop
    // describing a page the user has left.
    if (!location.pathname.includes('/map')) {
      resolvedOrgCode.current = null
      useMapStateStore.setState(useMapStateStore.getInitialState())
      setMapArrival({ status: 'pending' })
      return
    }
    // Every lookup is scoped to an operation, so there is nothing to resolve
    // against until one is known.
    if (orgCode === undefined) return

    // The store writes the URL with `replaceState`, which the router does not
    // see, so the router's search string can lag the address bar by every move
    // the user has made since arriving. We let the router tell us that
    // something changed and then read the params from the URL itself.
    const search = new URLSearchParams(window.location.search)
    // A navigation which leaves the map's params alone must not resolve them a
    // second time and discard the pane scopes the user has built since. We
    // compare against what the store would write rather than against the last
    // URL we resolved, because the store has rewritten the URL since then.
    const current = urlFor(selectUrlState(useMapStateStore.getState())).searchParams
    if (orgCode === resolvedOrgCode.current && sameParams(search, current)) return
    resolvedOrgCode.current = orgCode

    setMapArrival({ status: 'pending' })

    void runResolution(
      { kind: 'NavigateToAddress', address: readMapAddress(search) },
      createMapCatalog({ queryClient, apiClient, orgCode })
    ).then((outcome) => {
      // A superseded arrival reports nothing, because the newer request that
      // overtook it reports for itself.
      if (outcome.kind === 'Landed') setMapArrival({ status: 'arrived' })
      if (outcome.kind === 'Failed') reportMapArrivalFailure(outcome.failure)
    })
  }, [location, orgCode, apiClient, queryClient])
}

// ---------------------------------------------------------------------------
// store → URL
// ---------------------------------------------------------------------------

const writeAsAppliedToUrl = (
  search: URLSearchParams,
  layer: { streamId: string; applicationId: string; layerId: string; mergedStreamIds: string[] },
  prefix: Pane
) => {
  const k = (main: SearchParams, comp: SearchParams) => (prefix === Pane.Main ? main : comp)
  search.set(k(SearchParams.StreamId, SearchParams.ComparisonStreamId), layer.streamId)
  search.set(
    k(SearchParams.ApplicationId, SearchParams.ComparisonApplicationId),
    layer.applicationId
  )
  search.set(k(SearchParams.LayerId, SearchParams.ComparisonLayerId), layer.layerId)
  search.delete(k(SearchParams.MergedStreamId, SearchParams.ComparisonMergedStreamId))
  layer.mergedStreamIds.forEach((id) =>
    search.append(k(SearchParams.MergedStreamId, SearchParams.ComparisonMergedStreamId), id)
  )
}

const writePlanningToUrl = (
  search: URLSearchParams,
  layer: {
    planningFieldId: string
    planId: string | null
    assetType:
      | AssetType
      | typeof PLAN_EDITOR_ASSET_TYPE
      | typeof BOUNDARY_CREATOR_ASSET_TYPE
      | typeof PRESCRIPTION_CREATOR_ASSET_TYPE
    /** Absent for GuidancePlan composites and the editors. */
    assetId?: string
  },
  prefix: Pane
) => {
  const k = (main: SearchParams, comp: SearchParams) => (prefix === Pane.Main ? main : comp)
  writePlanningFieldToUrl(search, layer.planningFieldId, prefix)
  if (layer.planId === null) {
    search.delete(k(SearchParams.PlanId, SearchParams.ComparisonPlanId))
  } else {
    search.set(k(SearchParams.PlanId, SearchParams.ComparisonPlanId), layer.planId)
  }
  search.set(k(SearchParams.AssetType, SearchParams.ComparisonAssetType), layer.assetType)
  if (layer.assetId === undefined) {
    search.delete(k(SearchParams.AssetId, SearchParams.ComparisonAssetId))
  } else {
    search.set(k(SearchParams.AssetId, SearchParams.ComparisonAssetId), layer.assetId)
  }
}

/**
 * The planning field id, which every field-scoped kind carries — a bare field
 * writes nothing else, so this is the whole of that case.
 */
const writePlanningFieldToUrl = (search: URLSearchParams, planningFieldId: string, prefix: Pane) =>
  search.set(
    prefix === Pane.Main ? SearchParams.PlanningFieldId : SearchParams.ComparisonPlanningFieldId,
    planningFieldId
  )

const writeTerrainToUrl = (search: URLSearchParams, layer: TerrainLayer, prefix: Pane) => {
  const k = (main: SearchParams, comp: SearchParams) => (prefix === Pane.Main ? main : comp)
  writePlanningFieldToUrl(search, layer.planningFieldId, prefix)
  search.set(k(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource), layer.kind)

  if (layer.kind === 'Satellite') {
    search.set(k(SearchParams.Imagery, SearchParams.ComparisonImagery), layer.imagery)
    const captureDateKey = k(SearchParams.CaptureDate, SearchParams.ComparisonCaptureDate)
    if (layer.captureDate === null) {
      search.delete(captureDateKey)
    } else {
      search.set(captureDateKey, layer.captureDate)
    }
    return
  }

  if (layer.kind === 'SoilSurvey') {
    search.set(
      k(SearchParams.SoilSurveyAttribute, SearchParams.ComparisonSoilSurveyAttribute),
      layer.attribute
    )
    return
  }

  search.set(
    k(SearchParams.SamplingEventId, SearchParams.ComparisonSamplingEventId),
    layer.samplingEventId
  )
  search.set(k(SearchParams.Measurement, SearchParams.ComparisonMeasurement), layer.measurement)
}

const writeLayerToUrl = (search: URLSearchParams, layer: LayerSelection, prefix: Pane) => {
  clearLayerParams(search, prefix)

  if (layer.kind === 'AsApplied') return writeAsAppliedToUrl(search, layer, prefix)
  if (isTerrainLayer(layer)) return writeTerrainToUrl(search, layer, prefix)
  if (layer.kind === 'Field') return writePlanningFieldToUrl(search, layer.planningFieldId, prefix)
  if (layer.kind === 'BoundaryCreator') {
    return writePlanningToUrl(
      search,
      {
        planningFieldId: layer.planningFieldId,
        planId: null,
        assetType: BOUNDARY_CREATOR_ASSET_TYPE
      },
      prefix
    )
  }
  if (layer.kind === 'PrescriptionCreator') {
    return writePlanningToUrl(
      search,
      {
        planningFieldId: layer.planningFieldId,
        planId: null,
        assetType: PRESCRIPTION_CREATOR_ASSET_TYPE
      },
      prefix
    )
  }

  writePlanningToUrl(
    search,
    layer.kind === 'PlanEditor'
      ? {
          planningFieldId: layer.planningFieldId,
          planId: layer.planId,
          assetType: PLAN_EDITOR_ASSET_TYPE
        }
      : {
          planningFieldId: layer.planningFieldId,
          planId: layer.planId,
          assetType: layer.assetType,
          assetId: 'assetId' in layer ? layer.assetId : undefined
        },
    prefix
  )
}

/**
 * Drop every param any layer kind writes for a pane. A pane holds exactly one
 * selection, so a write clears the lot first and then sets only its own — which
 * is what lets the kinds share params (the planning field id) instead of each
 * needing its own spelling of the same identifier.
 */
const clearLayerParams = (search: URLSearchParams, prefix: Pane) => {
  const k = (main: SearchParams, comp: SearchParams) => (prefix === Pane.Main ? main : comp)
  search.delete(k(SearchParams.StreamId, SearchParams.ComparisonStreamId))
  search.delete(k(SearchParams.ApplicationId, SearchParams.ComparisonApplicationId))
  search.delete(k(SearchParams.LayerId, SearchParams.ComparisonLayerId))
  search.delete(k(SearchParams.MergedStreamId, SearchParams.ComparisonMergedStreamId))
  search.delete(k(SearchParams.PlanningFieldId, SearchParams.ComparisonPlanningFieldId))
  search.delete(k(SearchParams.PlanId, SearchParams.ComparisonPlanId))
  search.delete(k(SearchParams.AssetType, SearchParams.ComparisonAssetType))
  search.delete(k(SearchParams.AssetId, SearchParams.ComparisonAssetId))
  search.delete(k(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource))
  search.delete(k(SearchParams.Imagery, SearchParams.ComparisonImagery))
  search.delete(k(SearchParams.CaptureDate, SearchParams.ComparisonCaptureDate))
  search.delete(k(SearchParams.SamplingEventId, SearchParams.ComparisonSamplingEventId))
  search.delete(k(SearchParams.Measurement, SearchParams.ComparisonMeasurement))
  search.delete(k(SearchParams.SoilSurveyAttribute, SearchParams.ComparisonSoilSurveyAttribute))
}

const writeBrowseToUrl = (search: URLSearchParams, browse: BrowseFields) => {
  if (browse.filter.level === 'None') {
    search.delete(SearchParams.ClientFilter)
    search.delete(SearchParams.FarmFilter)
  } else {
    search.set(SearchParams.ClientFilter, browse.filter.client)
    if (browse.filter.level === 'Farm') {
      search.set(SearchParams.FarmFilter, browse.filter.farm)
    } else {
      search.delete(SearchParams.FarmFilter)
    }
  }

  if (browse.seasonFilter === null) {
    search.delete(SearchParams.SeasonFilterYear)
    search.delete(SearchParams.SeasonFilterCrop)
  } else {
    search.set(SearchParams.SeasonFilterYear, String(browse.seasonFilter.year))
    search.set(SearchParams.SeasonFilterCrop, browse.seasonFilter.cropKey)
  }

  if (browse.listView === 'ByField') {
    search.delete(SearchParams.ListView)
  } else {
    search.set(SearchParams.ListView, browse.listView)
  }
}

const clearBrowseParams = (search: URLSearchParams) => {
  search.delete(SearchParams.ClientFilter)
  search.delete(SearchParams.FarmFilter)
  search.delete(SearchParams.SeasonFilterYear)
  search.delete(SearchParams.SeasonFilterCrop)
  search.delete(SearchParams.ListView)
}

/**
 * Exactly what the URL carries. `syncToUrl` can read nothing else, so a new param has to be
 * added here, where the subscription also learns to fire for it.
 */
type UrlState = Pick<MapState, 'session' | 'mode' | 'record'> & { activeNoteId: string | null }

const selectUrlState = (state: MapState): UrlState => ({
  session: state.session,
  mode: state.mode,
  record: state.record,
  activeNoteId: state.scoutingSession?.activeNoteId ?? null
})

const sameParams = (a: URLSearchParams, b: URLSearchParams) => {
  const sorted = (params: URLSearchParams) => {
    const copy = new URLSearchParams(params)
    copy.sort()
    return copy.toString()
  }
  return sorted(a) === sorted(b)
}

const urlFor = (state: UrlState): URL => {
  const url = new URL(window.location.href)
  const search = url.searchParams

  if (state.session.kind === 'BrowseFields') {
    clearLayerParams(search, Pane.Main)
    clearLayerParams(search, Pane.Comparison)
    search.delete(SearchParams.SplitHidden)
    writeBrowseToUrl(search, state.session)
  } else {
    clearBrowseParams(search)
    writeLayerToUrl(search, state.session.mainMap, Pane.Main)

    if (state.session.comparisonMap === null) {
      clearLayerParams(search, Pane.Comparison)
      search.delete(SearchParams.SplitHidden)
    } else {
      writeLayerToUrl(search, state.session.comparisonMap.selection, Pane.Comparison)
      if (state.session.comparisonMap.disabled) {
        search.set(SearchParams.SplitHidden, '1')
      } else {
        search.delete(SearchParams.SplitHidden)
      }
    }
  }

  if (state.record === LATEST_DATA) {
    search.delete(SearchParams.Record)
  } else {
    search.set(SearchParams.Record, String(state.record))
  }

  search.set(SearchParams.MapMode, state.mode)

  if (state.activeNoteId === null) {
    search.delete(SearchParams.ScoutingNoteId)
  } else {
    search.set(SearchParams.ScoutingNoteId, state.activeNoteId)
  }

  return url
}

const syncToUrl = (state: UrlState) => {
  if (!window.location.pathname.includes('/map')) return
  window.history.replaceState({}, '', urlFor(state).toString())
}

/**
 * Safari refuses more than 100 `replaceState` calls in ten seconds, and a scrubber drag can
 * change the store faster than that. The write reads the store when it fires, so a collapsed
 * burst still ends on the latest state.
 */
const URL_WRITE_WINDOW_MS = 250

export const subscribeToUrlSync = () => {
  const write = throttle(
    () => syncToUrl(selectUrlState(useMapStateStore.getState())),
    URL_WRITE_WINDOW_MS
  )
  return useMapStateStore.subscribe(selectUrlState, write, { equalityFn: shallow })
}

// ---------------------------------------------------------------------------
// address → URL
// ---------------------------------------------------------------------------

/**
 * A link to the map page showing `address`, for the places that reach the map
 * from outside it. Off the map page the store is not the authority — arriving
 * re-reads state from the URL — so a link is how another page names a
 * destination, and {@link MapAddress} is that destination in the form a link
 * can carry it.
 *
 * Shares the param spellings with the reader above, so a link written here and
 * a URL read there cannot drift apart.
 *
 * `prefix` is the map route itself, which this package does not know: each
 * client routes its own way.
 */
export const mapLink = (prefix: string, address: MapAddress): string => {
  const search = new URLSearchParams()

  if (address.kind === 'Browse') {
    writeBrowseToUrl(search, browseFieldsFrom(address))
    return `${prefix}?${search.toString()}`
  }

  writeLayerAddressToUrl(search, address.main, Pane.Main)
  if (address.comparison !== undefined) {
    writeLayerAddressToUrl(search, address.comparison.layer, Pane.Comparison)
    if (address.comparison.hidden) search.set(SearchParams.SplitHidden, '1')
  }

  if (address.mode !== undefined) search.set(SearchParams.MapMode, address.mode)
  if (address.record !== undefined) search.set(SearchParams.Record, String(address.record))
  if (address.activeNoteId !== undefined) {
    search.set(SearchParams.ScoutingNoteId, address.activeNoteId)
  }

  return `${prefix}?${search.toString()}`
}

const browseFieldsFrom = (address: BrowseAddress): BrowseFields => ({
  ...defaultBrowseFields,
  filter: address.filter ?? defaultBrowseFields.filter,
  seasonFilter: address.seasonFilter ?? null,
  listView: address.listView ?? defaultBrowseFields.listView
})

/**
 * One pane's address. An address may name less than a selection does — a stream
 * with no application and layer, a satellite capture with no date — and each
 * omission is left out of the URL rather than filled in here, so that reading
 * it back closes it the same way a cold load would.
 */
const writeLayerAddressToUrl = (search: URLSearchParams, address: LayerAddress, prefix: Pane) => {
  const k = (main: SearchParams, comp: SearchParams) => (prefix === Pane.Main ? main : comp)

  switch (address.kind) {
    case 'Cff':
      search.set(SearchParams.Client, address.cff.client)
      search.set(SearchParams.Farm, address.cff.farm)
      search.set(SearchParams.Field, address.cff.field)
      return

    case 'Field':
      return writePlanningFieldToUrl(search, address.planningFieldId, prefix)

    case 'Stream':
      search.set(k(SearchParams.StreamId, SearchParams.ComparisonStreamId), address.streamId)
      if (address.layer !== undefined) {
        search.set(
          k(SearchParams.ApplicationId, SearchParams.ComparisonApplicationId),
          address.layer.applicationId
        )
        search.set(k(SearchParams.LayerId, SearchParams.ComparisonLayerId), address.layer.layerId)
      }
      address.mergedStreamIds?.forEach((id) =>
        search.append(k(SearchParams.MergedStreamId, SearchParams.ComparisonMergedStreamId), id)
      )
      return

    case 'PlanAsset':
      writePlanningToUrl(
        search,
        {
          planningFieldId: address.planningFieldId,
          planId: address.planId ?? null,
          assetType: address.asset.assetType,
          assetId: 'assetId' in address.asset ? address.asset.assetId : undefined
        },
        prefix
      )
      return

    case 'PlanEditor':
      writePlanningToUrl(
        search,
        {
          planningFieldId: address.planningFieldId,
          planId: address.planId,
          assetType: PLAN_EDITOR_ASSET_TYPE
        },
        prefix
      )
      return

    case 'BoundaryCreator':
      writePlanningToUrl(
        search,
        {
          planningFieldId: address.planningFieldId,
          planId: null,
          assetType: BOUNDARY_CREATOR_ASSET_TYPE
        },
        prefix
      )
      return

    case 'PrescriptionCreator':
      writePlanningToUrl(
        search,
        {
          planningFieldId: address.planningFieldId,
          planId: null,
          assetType: PRESCRIPTION_CREATOR_ASSET_TYPE
        },
        prefix
      )
      return

    case 'Satellite':
      writePlanningFieldToUrl(search, address.planningFieldId, prefix)
      search.set(k(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource), 'Satellite')
      if (address.imagery !== undefined) {
        search.set(k(SearchParams.Imagery, SearchParams.ComparisonImagery), address.imagery)
      }
      if (address.captureDate !== undefined) {
        search.set(
          k(SearchParams.CaptureDate, SearchParams.ComparisonCaptureDate),
          address.captureDate
        )
      }
      return

    case 'SoilSample':
      writePlanningFieldToUrl(search, address.planningFieldId, prefix)
      search.set(k(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource), 'SoilSample')
      search.set(
        k(SearchParams.SamplingEventId, SearchParams.ComparisonSamplingEventId),
        address.samplingEventId
      )
      search.set(
        k(SearchParams.Measurement, SearchParams.ComparisonMeasurement),
        address.measurement
      )
      return

    case 'SoilSurvey':
      writePlanningFieldToUrl(search, address.planningFieldId, prefix)
      search.set(k(SearchParams.TerrainSource, SearchParams.ComparisonTerrainSource), 'SoilSurvey')
      search.set(
        k(SearchParams.SoilSurveyAttribute, SearchParams.ComparisonSoilSurveyAttribute),
        address.attribute
      )
  }
}
